import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';

import type { CarouselConfig } from '@/shared/models/slider';
import { useScreenSize } from '@/shared/utils/breakpoints';

type CarouselVariant<T> = Omit<CarouselConfig<T>, 'slides'>;

interface UseCarouselConfigParams<T> {
  slides: MaybeRefOrGetter<T[]>;
  mobileMini: CarouselVariant<T>;
  tabletMobile: CarouselVariant<T>;
  desktop: CarouselVariant<T>;
  // Некоторые блоки считают «планшет» только диапазоном 768–1023 (isTabletOnly),
  // другие — всё до 1023 включительно (isTablet).
  tabletMatcher?: 'isTablet' | 'isTabletOnly';
}

// Выбирает адаптивную конфигурацию карусели по текущему брейкпоинту
// (mobileMini → планшет/мобайл → десктоп) и подставляет слайды.
export const useCarouselConfig = <T>(params: UseCarouselConfigParams<T>): ComputedRef<CarouselConfig<T>> => {
  const breakpoints = useScreenSize();

  return computed(() => {
    const slides = toValue(params.slides);

    if (breakpoints.isMobileMini.value) {
      return { ...params.mobileMini, slides };
    }

    const isTabletOrMobile =
      (params.tabletMatcher === 'isTabletOnly' ? breakpoints.isTabletOnly.value : breakpoints.isTablet.value) ||
      breakpoints.isMobile.value;

    if (isTabletOrMobile) {
      return { ...params.tabletMobile, slides };
    }

    return { ...params.desktop, slides };
  });
};
